import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import { api, type CrmOrder, type CrmOrderItemComponent } from "@/lib/api";
import { groupOrderComponents } from "./orderComponentUtils";

type SortField = "created_at" | "order_number" | "status" | "total_amount";
type SortDir = "asc" | "desc";
type OrderSourceFilter = "" | "web" | "telegram";
type OrderStatusFilter = "" | "payment_pending" | "paid" | "processing" | "completed" | "cancelled" | "refunded";

interface OrderFilters {
  q: string;
  source: OrderSourceFilter;
  status: OrderStatusFilter;
  date_from: string;
  date_to: string;
}

const EMPTY_FILTERS: OrderFilters = {
  q: "",
  source: "",
  status: "",
  date_from: "",
  date_to: "",
};

const STATUS_LABELS: Record<string, string> = {
  payment_pending: "Ожидает оплаты",
  paid: "Оплачен",
  processing: "В работе",
  completed: "Завершен",
  cancelled: "Отменен",
  refunded: "Возврат",
};

const STATUS_STYLES: Record<string, string> = {
  payment_pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  processing: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-rose-50 text-rose-700 border-rose-200",
};

const SOURCE_LABELS: Record<string, string> = {
  web: "Сайт",
  telegram: "Telegram",
};

const STATUS_OPTIONS: { value: OrderStatusFilter; label: string }[] = [
  { value: "", label: "Все статусы" },
  { value: "payment_pending", label: STATUS_LABELS.payment_pending },
  { value: "paid", label: STATUS_LABELS.paid },
  { value: "processing", label: STATUS_LABELS.processing },
  { value: "completed", label: STATUS_LABELS.completed },
  { value: "cancelled", label: STATUS_LABELS.cancelled },
  { value: "refunded", label: STATUS_LABELS.refunded },
];

const SOURCE_OPTIONS: { value: OrderSourceFilter; label: string }[] = [
  { value: "", label: "Все источники" },
  { value: "web", label: "Сайт" },
  { value: "telegram", label: "Telegram" },
];

const STATUS_ACTIONS: Record<string, { status: OrderStatusFilter; label: string; tone: string; icon: string }[]> = {
  payment_pending: [
    { status: "paid", label: "Отметить оплаченным", tone: "text-emerald-700", icon: "ri-bank-card-line" },
    { status: "cancelled", label: "Отменить", tone: "text-red-700", icon: "ri-close-circle-line" },
  ],
  paid: [
    { status: "processing", label: "В работу", tone: "text-orange-700", icon: "ri-tools-line" },
    { status: "refunded", label: "Возврат", tone: "text-rose-700", icon: "ri-refund-2-line" },
  ],
  processing: [
    { status: "completed", label: "Завершить", tone: "text-green-700", icon: "ri-check-double-line" },
    { status: "refunded", label: "Возврат", tone: "text-rose-700", icon: "ri-refund-2-line" },
  ],
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value: string | number) {
  return `${Number(value || 0).toLocaleString("ru-RU")} ₽`;
}

function OrderComponentGroups({ components }: { components: CrmOrderItemComponent[] }) {
  const { setItems, gifts } = groupOrderComponents(components);
  const groups = [
    { title: "Состав набора", icon: "ri-list-check-3", items: setItems },
    { title: "Подарки", icon: "ri-gift-line", items: gifts },
  ].filter((group) => group.items.length > 0);

  return (
    <div className="mt-3 space-y-2">
      {groups.map((group) => (
        <div key={group.title} className="rounded-lg border border-[#F0EDE6] bg-[#FAF8F5] px-3 py-2">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
            <i className={group.icon} />
            {group.title}
          </p>
          <div className="space-y-1.5">
            {group.items.map((component) => (
              <div key={component.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 text-xs text-gray-600">
                <div className="min-w-0">
                  <p className="break-words font-medium leading-5 text-gray-700">{component.title}</p>
                  {component.sku_label || component.comment ? (
                    <p className="mt-0.5 break-words leading-5 text-gray-500">
                      {[component.sku_label, component.comment].filter(Boolean).join(" · ")}
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 text-gray-500">× {component.quantity}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function getStatusUpdateErrorMessage(error: unknown) {
  if (!(error instanceof Error)) {
    return "Не удалось обновить статус заказа";
  }

  if (error.message === "Not Found" || error.message === "Order not found") {
    return "В текущем backend-контуре смена статуса пока недоступна. Проверьте, что CRM подключена к API с поддержкой PATCH /crm/orders/{id}/status.";
  }

  return error.message;
}

function normalizeFilters(filters: OrderFilters) {
  return {
    q: filters.q.trim() || undefined,
    source: filters.source || undefined,
    status: filters.status || undefined,
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
  };
}

function OrderStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
        STATUS_STYLES[status] || "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) {
    return <i className="ri-arrow-up-down-line text-xs text-gray-300" />;
  }
  return <i className={`${dir === "asc" ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-xs text-amber-600`} />;
}

export default function OrdersPage() {
  const [orders, setOrders] = useState<CrmOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<CrmOrder | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  const [managerCommentDraft, setManagerCommentDraft] = useState("");
  const [managerCommentSaving, setManagerCommentSaving] = useState(false);
  const [managerCommentError, setManagerCommentError] = useState<string | null>(null);

  const loadOrders = useCallback((nextFilters: OrderFilters) => {
    setLoading(true);
    setError(null);
    setAppliedFilters(nextFilters);
    return api
      .orders(normalizeFilters(nextFilters))
      .then((nextOrders) => {
        setOrders(nextOrders);
        setSelectedOrder((current) => (current ? nextOrders.find((item) => item.id === current.id) ?? current : current));
        return nextOrders;
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Не удалось загрузить заказы");
        throw err;
      })
      .finally(() => setLoading(false));
  }, []);

  const loadOrderDetail = useCallback((orderId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    api
      .order(orderId)
      .then((order) => setSelectedOrder(order))
      .catch((err) => setDetailError(err instanceof Error ? err.message : "Не удалось загрузить детали заказа"))
      .finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => {
    void loadOrders(EMPTY_FILTERS);
  }, [loadOrders]);

  useEffect(() => {
    setManagerCommentDraft(selectedOrder?.manager_comment ?? "");
    setManagerCommentError(null);
  }, [selectedOrder?.id, selectedOrder?.manager_comment]);

  const sortedOrders = useMemo(() => {
    const next = [...orders];
    next.sort((a, b) => {
      let compare = 0;
      if (sortField === "created_at") {
        compare = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortField === "order_number") {
        compare = a.order_number.localeCompare(b.order_number, "ru");
      } else if (sortField === "status") {
        compare = a.status.localeCompare(b.status, "ru");
      } else {
        compare = Number(a.total_amount) - Number(b.total_amount);
      }
      return sortDir === "asc" ? compare : -compare;
    });
    return next;
  }, [orders, sortDir, sortField]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDir(field === "status" ? "asc" : "desc");
  };

  const openOrderPanel = (order: CrmOrder) => {
    setSelectedOrderId(order.id);
    setSelectedOrder(order);
    setDetailError(null);
    void loadOrderDetail(order.id);
  };

  const closeOrderPanel = () => {
    setSelectedOrderId(null);
    setSelectedOrder(null);
    setDetailError(null);
    setStatusUpdateError(null);
    setManagerCommentError(null);
    setManagerCommentDraft("");
    setDetailLoading(false);
    setStatusUpdating(false);
    setManagerCommentSaving(false);
  };

  const replaceOrderInState = useCallback((updatedOrder: CrmOrder) => {
    setOrders((prev) => {
      const exists = prev.some((order) => order.id === updatedOrder.id);
      return exists ? prev.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)) : prev;
    });
    setSelectedOrder(updatedOrder);
  }, []);

  const handleStatusChange = useCallback(
    async (nextStatus: OrderStatusFilter) => {
      if (!selectedOrder || !nextStatus || statusUpdating) return;

      setStatusUpdating(true);
      setStatusUpdateError(null);
      try {
        const updatedOrder = await api.updateOrderStatus(selectedOrder.id, { status: nextStatus });
        replaceOrderInState(updatedOrder);
        await loadOrders(appliedFilters);
      } catch (err) {
        setStatusUpdateError(getStatusUpdateErrorMessage(err));
      } finally {
        setStatusUpdating(false);
      }
    },
    [appliedFilters, loadOrders, replaceOrderInState, selectedOrder, statusUpdating],
  );

  const handleManagerCommentSave = useCallback(async () => {
    if (!selectedOrder || managerCommentSaving) return;

    setManagerCommentSaving(true);
    setManagerCommentError(null);
    try {
      const updatedOrder = await api.updateOrderManagerComment(selectedOrder.id, {
        manager_comment: managerCommentDraft.trim() || null,
      });
      replaceOrderInState(updatedOrder);
      await loadOrders(appliedFilters);
    } catch (err) {
      setManagerCommentError(err instanceof Error ? err.message : "Не удалось сохранить комментарий менеджера");
    } finally {
      setManagerCommentSaving(false);
    }
  }, [appliedFilters, loadOrders, managerCommentDraft, managerCommentSaving, replaceOrderInState, selectedOrder]);

  const handleApplyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadOrders(filters);
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    void loadOrders(EMPTY_FILTERS);
  };

  const handleOpenGoogleSheet = () => {
    window.open(api.ordersGoogleSheetUrl(), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Заказы</h2>
            <p className="mt-0.5 text-xs text-gray-500">Всего заказов в выборке: {sortedOrders.length}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleOpenGoogleSheet}
              className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:border-amber-400 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-google-line" />
              Открыть Google Sheets
            </button>
            <button
              type="button"
              onClick={() => void loadOrders(appliedFilters)}
              className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:border-amber-400 hover:text-gray-900"
            >
              <i className="ri-refresh-line" />
              Обновить
            </button>
          </div>
        </div>

        <form onSubmit={handleApplyFilters} className="rounded-xl border border-[#E8E2D8] bg-white p-4">
          <div className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_180px_180px_160px_160px_auto]">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Поиск</label>
              <input
                type="text"
                value={filters.q}
                onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                placeholder="Номер заказа, телефон, email"
                className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Источник</label>
              <select
                value={filters.source}
                onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value as OrderSourceFilter }))}
                className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Статус</label>
              <select
                value={filters.status}
                onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value as OrderStatusFilter }))}
                className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Дата от</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))}
                className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Дата до</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))}
                className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
              >
                <i className="ri-search-line" />
                Применить
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#E8E2D8] bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-amber-400 hover:text-gray-900"
              >
                <i className="ri-close-circle-line" />
                Сбросить
              </button>
            </div>
          </div>
        </form>

        {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

        <div className="overflow-hidden rounded-xl border border-[#E8E2D8] bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <i className="ri-loader-4-line animate-spin text-xl text-amber-600" />
              <span className="text-sm text-gray-500">Загрузка заказов...</span>
            </div>
          ) : sortedOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <i className="ri-file-list-3-line text-2xl text-gray-300" />
              <span className="text-sm text-gray-500">Заказы не найдены</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[#E8E2D8] bg-[#FAF8F5]">
                    <th onClick={() => toggleSort("created_at")} className="cursor-pointer whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        Дата
                        <SortIcon active={sortField === "created_at"} dir={sortDir} />
                      </span>
                    </th>
                    <th onClick={() => toggleSort("order_number")} className="cursor-pointer whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        Номер
                        <SortIcon active={sortField === "order_number"} dir={sortDir} />
                      </span>
                    </th>
                    <th onClick={() => toggleSort("status")} className="cursor-pointer whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        Статус
                        <SortIcon active={sortField === "status"} dir={sortDir} />
                      </span>
                    </th>
                    <th onClick={() => toggleSort("total_amount")} className="cursor-pointer whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        Сумма
                        <SortIcon active={sortField === "total_amount"} dir={sortDir} />
                      </span>
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">Клиент</th>
                    <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">Телефон</th>
                    <th className="hidden whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600 lg:table-cell">Источник</th>
                    <th className="w-12 px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {sortedOrders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => openOrderPanel(order)}
                      className="cursor-pointer border-b border-[#F0EDE6] transition-colors hover:bg-[#FAF8F5]/60"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatDate(order.created_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-800">{order.order_number}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <OrderStatusBadge status={order.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-800">{formatMoney(order.total_amount)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{order.recipient.full_name}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{order.recipient.phone}</td>
                      <td className="hidden whitespace-nowrap px-4 py-3 text-sm text-gray-600 lg:table-cell">
                        {SOURCE_LABELS[order.source] || order.source}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        <i className="ri-arrow-right-s-line text-lg" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedOrderId ? (
        <div className="fixed inset-0 z-50">
          <button className="absolute inset-0 bg-black/25" aria-label="Закрыть панель заказа" onClick={closeOrderPanel} />
          <aside className="absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col border-l border-[#E8E2D8] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#E8E2D8] px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Карточка заказа</p>
                <h3 className="mt-1 text-lg font-semibold text-gray-900">{selectedOrder?.order_number || "Загрузка..."}</h3>
              </div>
              <button
                onClick={closeOrderPanel}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {detailError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{detailError}</div>
              ) : null}
              {statusUpdateError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{statusUpdateError}</div>
              ) : null}

              {selectedOrder ? (
                <div className="space-y-5">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-xl border border-[#E8E2D8] bg-[#FAF8F5] p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Статус</p>
                      <div className="mt-3">
                        <OrderStatusBadge status={selectedOrder.status} />
                      </div>
                    </div>
                    <div className="rounded-xl border border-[#E8E2D8] bg-[#FAF8F5] p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Источник</p>
                      <p className="mt-3 text-sm font-medium text-gray-800">{SOURCE_LABELS[selectedOrder.source] || selectedOrder.source}</p>
                    </div>
                    <div className="rounded-xl border border-[#E8E2D8] bg-[#FAF8F5] p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Сумма заказа</p>
                      <p className="mt-3 text-sm font-semibold text-gray-900">{formatMoney(selectedOrder.total_amount)}</p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Действия со статусом</p>
                        <p className="mt-2 text-sm text-gray-600">Изменения сохраняются сразу в backend и обновляют реальный заказ.</p>
                      </div>
                      <div className="w-full max-w-56">
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Ручной выбор</label>
                        <select
                          value={selectedOrder.status}
                          onChange={(event) => void handleStatusChange(event.target.value as OrderStatusFilter)}
                          disabled={statusUpdating}
                          className="w-full rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {STATUS_OPTIONS.filter((option) => option.value).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {STATUS_ACTIONS[selectedOrder.status]?.length ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {STATUS_ACTIONS[selectedOrder.status].map((action) => (
                          <button
                            key={action.status}
                            type="button"
                            onClick={() => void handleStatusChange(action.status)}
                            disabled={statusUpdating}
                            className={`inline-flex items-center gap-2 rounded-lg border border-[#E8E2D8] bg-white px-3 py-2 text-sm font-medium transition-colors hover:border-amber-400 disabled:cursor-not-allowed disabled:opacity-60 ${action.tone}`}
                          >
                            <i className={action.icon} />
                            {action.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-lg border border-[#E8E2D8] bg-[#FAF8F5] px-3 py-2 text-sm text-gray-600">
                        Для этого статуса быстрых действий нет.
                      </div>
                    )}

                    {statusUpdating ? (
                      <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-loader-4-line animate-spin text-amber-600" />
                        Сохраняем новый статус...
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Комментарий менеджера</p>
                        <p className="mt-2 text-sm text-gray-600">Внутренняя заметка видна только в CRM.</p>
                      </div>
                      {selectedOrder.manager_comment ? (
                        <span className="inline-flex w-fit items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                          <i className="ri-sticky-note-line" />
                          Есть комментарий
                        </span>
                      ) : null}
                    </div>
                    <textarea
                      value={managerCommentDraft}
                      onChange={(event) => setManagerCommentDraft(event.target.value)}
                      maxLength={2000}
                      rows={4}
                      placeholder="Например: позвонить клиенту перед доставкой"
                      className="mt-4 w-full resize-y rounded-lg border border-[#E8E2D8] bg-[#FAFAF8] px-3 py-2 text-sm leading-6 text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                    />
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-gray-400">{managerCommentDraft.length.toLocaleString("ru-RU")} / 2000</p>
                      <button
                        type="button"
                        onClick={() => void handleManagerCommentSave()}
                        disabled={managerCommentSaving || managerCommentDraft.trim() === (selectedOrder.manager_comment ?? "")}
                        className="inline-flex w-fit items-center justify-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {managerCommentSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />}
                        {managerCommentSaving ? "Сохраняем..." : "Сохранить комментарий"}
                      </button>
                    </div>
                    {managerCommentError ? (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{managerCommentError}</div>
                    ) : null}
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Получатель</p>
                      <p className="mt-3 text-sm font-medium text-gray-900">{selectedOrder.recipient.full_name}</p>
                      <p className="mt-1 text-sm text-gray-600">{selectedOrder.recipient.phone}</p>
                      {selectedOrder.recipient.email ? (
                        <p className="mt-1 text-sm text-gray-600">{selectedOrder.recipient.email}</p>
                      ) : (
                        <p className="mt-1 text-sm text-gray-400">Email не указан</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Даты</p>
                      <p className="mt-3 text-sm text-gray-700">
                        <span className="font-medium text-gray-900">Создан:</span> {formatDate(selectedOrder.created_at)}
                      </p>
                      <p className="mt-1 text-sm text-gray-700">
                        <span className="font-medium text-gray-900">Оплачен:</span>{" "}
                        {selectedOrder.paid_at ? formatDate(selectedOrder.paid_at) : "еще не оплачен"}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E8E2D8] bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Адрес доставки</p>
                    <p className="mt-3 text-sm leading-6 text-gray-700">{selectedOrder.recipient.delivery_address}</p>
                  </div>

                  <div className="rounded-xl border border-[#E8E2D8] bg-white">
                    <div className="border-b border-[#E8E2D8] px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">Состав заказа</p>
                    </div>
                    <div className="divide-y divide-[#F0EDE6]">
                      {selectedOrder.items.map((item) => (
                        <div key={item.id} className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(260px,1fr)_120px_70px_110px]">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.title}</p>
                            <p className="mt-1 text-xs text-gray-500">{item.sku_label || "SKU не указан"}</p>
                            {item.is_bundle_component ? (
                              <span className="mt-2 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                                bundle item
                              </span>
                            ) : null}
                            {item.components?.length ? <OrderComponentGroups components={item.components} /> : null}
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Цена</p>
                            <p className="mt-1 text-sm text-gray-700">{formatMoney(item.price)}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Кол-во</p>
                            <p className="mt-1 text-sm text-gray-700">{item.quantity}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Сумма</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">{formatMoney(item.subtotal)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {detailLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <i className="ri-loader-4-line animate-spin text-amber-600" />
                      Обновляем детали заказа...
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="flex h-full min-h-64 items-center justify-center">
                  <div className="flex flex-col items-center gap-3 text-gray-500">
                    <i className="ri-loader-4-line animate-spin text-xl text-amber-600" />
                    <span className="text-sm">Загрузка деталей заказа...</span>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}
